import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { Calendar, Clock, User, Scissors, ChevronLeft, ChevronRight, Check, Phone, Mail, Package, Plus, Minus, ShoppingBag, MapPin, MessageCircle } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { servicesAPI, barbersAPI, appointmentsAPI } from '../lib/api';
import { toast } from 'sonner';

// El Patron Products
const EL_PATRON_PRODUCTS = [
    { id: 'ep1', name: "Sea Drops", price: 15.99 },
    { id: 'ep2', name: "Cera in Polvere", price: 11.99 },
    { id: 'ep3', name: "Cera Lucida", price: 9.99 },
    { id: 'ep4', name: "Cera Matt", price: 10.99 },
    { id: 'ep5', name: "Cera Pomade", price: 10.99 },
    { id: 'ep6', name: "Curl Control", price: 15.99 },
    { id: 'ep7', name: "Gel", price: 11.99 },
    { id: 'ep8', name: "Gel Wax", price: 13.99 },
    { id: 'ep9', name: "Cera Liquida Modellante", price: 8.00 },
];

const BookingPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    // Booking state - NOW SUPPORTS MULTIPLE SERVICES
    const [step, setStep] = useState(1);
    const [selectedServices, setSelectedServices] = useState([]); // Array of services
    const [selectedProducts, setSelectedProducts] = useState([]); // Array of products with quantities
    const [selectedBranch, setSelectedBranch] = useState('swieqi');
    const [selectedBarber, setSelectedBarber] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const [customerInfo, setCustomerInfo] = useState({
        name: '',
        email: '',
        phone: '',
        notes: ''
    });
    
    // Data state
    const [services, setServices] = useState([]);
    const [barbers, setBarbers] = useState([]);
    const [timeSlots, setTimeSlots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [syncError, setSyncError] = useState('');

    const loadData = useCallback(async () => {
        setSyncError('');
        try {
            const [servicesRes, barbersRes] = await Promise.all([
                servicesAPI.getAll(),
                barbersAPI.getActive()
            ]);
            setServices(servicesRes.data);
            setBarbers(barbersRes.data);

            if (!servicesRes.data?.length) {
                setSyncError('No services returned from Setmore. Tap retry.');
            } else if (!barbersRes.data?.length) {
                setSyncError('No barbers returned from Setmore. Tap retry.');
            }
        } catch (error) {
            console.error('Failed to load data:', error);
            toast.error('Failed to sync with Setmore');
            setSyncError('Unable to sync with Setmore. Tap retry.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const staffBranchMap = useMemo(() => {
        return barbers.reduce((acc, barber) => {
            const name = (barber?.name || '').toLowerCase();
            if (name.includes('swieqi')) {
                acc[barber.key] = 'swieqi';
            }
            if (name.includes('san gwann') || name.includes('san ġwann') || name.includes('sangwann')) {
                acc[barber.key] = 'san_gwann';
            }
            return acc;
        }, {});
    }, [barbers]);

    const getServiceBranches = useCallback((service) => {
        const branches = new Set();
        const name = (service?.name || '').toLowerCase();

        if (name.includes('swieqi')) {
            branches.add('swieqi');
        }
        if (name.includes('san gwann') || name.includes('san ġwann') || name.includes('sangwann')) {
            branches.add('san_gwann');
        }

        if (service?.staff_keys?.length) {
            service.staff_keys.forEach((key) => {
                const branch = staffBranchMap[key];
                if (branch) {
                    branches.add(branch);
                }
            });
        }

        if (branches.size === 0) {
            return ['swieqi', 'san_gwann'];
        }

        return Array.from(branches);
    }, [staffBranchMap]);

    const filteredServices = useMemo(() => {
        if (!selectedBranch) return services;
        return services.filter((service) => getServiceBranches(service).includes(selectedBranch));
    }, [services, selectedBranch, getServiceBranches]);

    const swieqiCount = useMemo(() => {
        return services.filter((service) => getServiceBranches(service).includes('swieqi')).length;
    }, [services, getServiceBranches]);

    const sanGwannCount = useMemo(() => {
        return services.filter((service) => getServiceBranches(service).includes('san_gwann')).length;
    }, [services, getServiceBranches]);

    // Filter barbers who can perform ALL selected services + branch
    const availableBarbers = useMemo(() => {
        const branchFiltered = selectedBranch
            ? barbers.filter((barber) => {
                const branch = staffBranchMap[barber.key];
                return branch ? branch === selectedBranch : true;
            })
            : barbers;

        if (selectedServices.length === 0) return branchFiltered;

        return branchFiltered.filter(barber => 
            selectedServices.every(service => 
                service.staff_keys?.includes(barber.key)
            )
        );
    }, [selectedServices, barbers, selectedBranch, staffBranchMap]);

    // Calculate total duration and price
    const totalDuration = useMemo(() => {
        return selectedServices.reduce((sum, s) => sum + (s.duration || 30), 0);
    }, [selectedServices]);

    const totalServicePrice = useMemo(() => {
        return selectedServices.reduce((sum, s) => sum + (s.price || 0), 0);
    }, [selectedServices]);

    const totalProductPrice = useMemo(() => {
        return selectedProducts.reduce((sum, p) => sum + (p.price * p.quantity), 0);
    }, [selectedProducts]);

    const totalPrice = totalServicePrice + totalProductPrice;

    useEffect(() => {
        const loadTimeSlots = async () => {
            if (!selectedDate || !selectedBarber || selectedServices.length === 0) return;
            
            try {
                const dateStr = format(selectedDate, 'yyyy-MM-dd');
                // Use the first service for slot calculation (Setmore limitation)
                const response = await appointmentsAPI.getAvailableSlots(
                    dateStr,
                    selectedBarber.key,
                    selectedServices[0].key
                );
                setTimeSlots(response.data);
            } catch (error) {
                console.error('Failed to load time slots:', error);
                setTimeSlots([]);
            }
        };
        loadTimeSlots();
    }, [selectedDate, selectedBarber, selectedServices]);

    const handleBranchSelect = (branch) => {
        if (branch === selectedBranch) return;
        setSelectedBranch(branch);
        setSelectedServices([]);
        setSelectedBarber(null);
        setSelectedDate(null);
        setSelectedTime(null);
        setTimeSlots([]);
    };

    // Toggle service selection
    const toggleService = (service) => {
        setSelectedServices(prev => {
            const exists = prev.find(s => s.key === service.key);
            if (exists) {
                return prev.filter(s => s.key !== service.key);
            } else {
                return [...prev, service];
            }
        });
        // Reset barber if they can't do the new combination
        setSelectedBarber(null);
    };

    // Toggle product selection
    const toggleProduct = (product) => {
        setSelectedProducts(prev => {
            const exists = prev.find(p => p.id === product.id);
            if (exists) {
                return prev.filter(p => p.id !== product.id);
            } else {
                return [...prev, { ...product, quantity: 1 }];
            }
        });
    };

    // Update product quantity
    const updateProductQuantity = (productId, delta) => {
        setSelectedProducts(prev => 
            prev.map(p => {
                if (p.id === productId) {
                    const newQty = Math.max(0, p.quantity + delta);
                    return newQty === 0 ? null : { ...p, quantity: newQty };
                }
                return p;
            }).filter(Boolean)
        );
    };

    const handleSubmit = async () => {
        if (selectedServices.length === 0 || !selectedBarber || !selectedDate || !selectedTime || !customerInfo.name || !customerInfo.email || !customerInfo.phone) {
            toast.error('Please fill in all required fields');
            return;
        }

        setSubmitting(true);
        try {
            // Create appointment with primary service (Setmore gets first service)
            // Products and additional services noted in comments
            const productsList = selectedProducts.map(p => `${p.name} x${p.quantity}`).join(', ');
            const additionalServices = selectedServices.slice(1).map(s => s.name).join(', ');
            
            let notes = customerInfo.notes || '';
            if (additionalServices) {
                notes += `\n[Additional services: ${additionalServices}]`;
            }
            if (productsList) {
                notes += `\n[El Patron Products: ${productsList}]`;
            }
            notes += `\n[Total: €${totalPrice.toFixed(2)}]`;

            await appointmentsAPI.create({
                service_key: selectedServices[0].key,
                staff_key: selectedBarber.key,
                date: format(selectedDate, 'yyyy-MM-dd'),
                time: selectedTime,
                customer_name: customerInfo.name,
                customer_email: customerInfo.email,
                customer_phone: customerInfo.phone,
                notes: notes.trim(),
                total_duration: totalDuration // Send total duration for all services
            });
            
            toast.success('Booking confirmed!');
            setStep(6); // Success step
        } catch (error) {
            console.error('Failed to book appointment:', error);
            toast.error(error.response?.data?.detail || 'Booking failed');
        } finally {
            setSubmitting(false);
        }
    };

    const steps = [
        { num: 1, label: 'Services', icon: Scissors },
        { num: 2, label: 'Products', icon: ShoppingBag },
        { num: 3, label: 'Barber', icon: User },
        { num: 4, label: 'Date & Time', icon: Calendar },
        { num: 5, label: 'Details', icon: Mail },
    ];

    const canProceed = () => {
        switch (step) {
            case 1: return selectedServices.length > 0;
            case 2: return true; // Products are optional
            case 3: return !!selectedBarber;
            case 4: return !!selectedDate && !!selectedTime;
            case 5: return customerInfo.name && customerInfo.email && customerInfo.phone;
            default: return false;
        }
    };

    return (
        <div className="min-h-screen bg-dark">
            <Navbar />

            <div className="pt-24 pb-28 px-6 md:px-12">
                <div className="max-w-4xl mx-auto">
                    {/* Header */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center mb-8"
                    >
                        <span className="text-accent-pink font-urbanist font-semibold text-sm mb-2 block">BOOK YOUR APPOINTMENT</span>
                        <h1 className="font-anton text-4xl md:text-5xl">CHOOSE YOUR SERVICES</h1>
                    </motion.div>

                    {/* Progress Steps */}
                    {step < 6 && (
                        <div className="flex justify-center mb-8 overflow-x-auto pb-2" data-testid="booking-progress">
                            {steps.map((s, i) => (
                                <div key={s.num} className="flex items-center">
                                    <div
                                        className={`flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full border-2 transition-colors ${
                                            step >= s.num
                                                ? 'border-primary bg-primary text-black'
                                                : 'border-zinc-700 text-zinc-600'
                                        }`}
                                    >
                                        {step > s.num ? <Check size={18} /> : <s.icon size={18} />}
                                    </div>
                                    {i < steps.length - 1 && (
                                        <div
                                            className={`w-8 md:w-16 h-0.5 ${
                                                step > s.num ? 'bg-primary' : 'bg-zinc-800'
                                            }`}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Cart Summary - Always visible during booking */}
                    {step < 6 && (selectedServices.length > 0 || selectedProducts.length > 0) && (
                        <div className="mb-6 p-4 bg-dark-paper border border-zinc-800 flex flex-wrap items-center justify-between gap-4">
                            <div className="flex flex-wrap gap-2">
                                {selectedServices.map(s => (
                                    <span key={s.key} className="px-3 py-1 bg-primary/20 text-primary text-sm font-urbanist">
                                        {s.name}
                                    </span>
                                ))}
                                {selectedProducts.map(p => (
                                    <span key={p.id} className="px-3 py-1 bg-amber-500/20 text-amber-500 text-sm font-urbanist">
                                        {p.name} x{p.quantity}
                                    </span>
                                ))}
                            </div>
                            <div className="font-anton text-xl text-primary">
                                €{totalPrice.toFixed(2)}
                            </div>
                        </div>
                    )}

                    {/* Setmore Sync Banner */}
                    {step === 1 && (
                        <div className="flex justify-center mb-6">
                            <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/30 text-primary text-sm font-urbanist">
                                <Check size={16} />
                                Synced with Setmore
                            </div>
                        </div>
                    )}

                    {step === 1 && syncError && (
                        <div className="mb-6 p-4 border border-accent-pink/40 bg-accent-pink/10 text-accent-pink flex flex-wrap items-center justify-between gap-3" data-testid="setmore-sync-error">
                            <span className="font-urbanist text-sm">{syncError}</span>
                            <button
                                type="button"
                                onClick={loadData}
                                className="px-4 py-2 border border-accent-pink text-accent-pink font-urbanist text-sm hover:bg-accent-pink hover:text-black"
                                data-testid="setmore-sync-retry"
                            >
                                Retry Sync
                            </button>
                        </div>
                    )}

                    {/* Step Content */}
                    <AnimatePresence mode="wait">
                        {/* Step 1: Service Selection (MULTI-SELECT) */}
                        {step === 1 && (
                            <motion.div
                                key="step1"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                data-testid="booking-step-1"
                            >
                                <h2 className="font-anton text-2xl mb-2">SELECT SERVICES</h2>
                                <p className="text-zinc-500 font-urbanist mb-6">You can select multiple services for one appointment</p>

                                <div className="mb-6" data-testid="booking-branch-selection">
                                    <p className="text-zinc-400 font-urbanist text-sm mb-3">Choose Location</p>
                                    <div className="grid sm:grid-cols-1 gap-3">
                                        <button
                                            type="button"
                                            onClick={() => handleBranchSelect('swieqi')}
                                            className={`border p-4 text-left transition-all ${
                                                selectedBranch === 'swieqi'
                                                    ? 'border-primary bg-primary/10'
                                                    : 'border-zinc-800 bg-dark-paper hover:border-zinc-600'
                                            }`}
                                            data-testid="booking-branch-swieqi"
                                        >
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <MapPin size={16} className="text-primary" />
                                                        <span className="font-anton text-xl text-white">SWIEQI</span>
                                                    </div>
                                                </div>
                                                <span className="text-zinc-500 text-xs font-urbanist">{swieqiCount} services</span>
                                            </div>
                                        </button>
                                    </div>
                                </div>
                                
                                {loading ? (
                                    <div className="space-y-4">
                                        {[1, 2, 3].map((i) => (
                                            <div key={i} className="bg-dark-paper border border-zinc-800 p-4 animate-pulse">
                                                <div className="h-6 bg-zinc-800 rounded w-1/3" />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="grid gap-3">
                                        {filteredServices.map((service) => {
                                            const isSelected = selectedServices.find(s => s.key === service.key);
                                            return (
                                                <button
                                                    key={service.key}
                                                    onClick={() => toggleService(service)}
                                                    data-testid={`service-select-${service.key}`}
                                                    className={`w-full text-left p-4 border transition-all ${
                                                        isSelected
                                                            ? 'border-primary bg-primary/10'
                                                            : 'border-zinc-800 bg-dark-paper hover:border-zinc-600'
                                                    }`}
                                                >
                                                    <div className="flex justify-between items-center">
                                                        <div className="flex items-center gap-4">
                                                            <div className={`w-6 h-6 border-2 flex items-center justify-center ${
                                                                isSelected ? 'border-primary bg-primary' : 'border-zinc-600'
                                                            }`}>
                                                                {isSelected && <Check size={14} className="text-black" />}
                                                            </div>
                                                            <div>
                                                                <h4 className="font-urbanist font-semibold text-white">
                                                                    {service.name}
                                                                </h4>
                                                                <p className="text-zinc-500 text-sm flex items-center gap-2 mt-1">
                                                                    <Clock size={14} />
                                                                    {service.duration} min
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <span className="font-anton text-xl text-primary">
                                                            €{service.price}
                                                        </span>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}

                                {selectedServices.length > 0 && (
                                    <div className="mt-6 p-4 bg-zinc-900 border border-zinc-800">
                                        <p className="text-zinc-400 font-urbanist text-sm">
                                            <span className="text-primary font-semibold">{selectedServices.length}</span> services selected • 
                                            Total duration: <span className="text-white">{totalDuration} min</span>
                                        </p>
                                    </div>
                                )}
                            </motion.div>
                        )}

                        {/* Step 2: Product Selection (OPTIONAL) */}
                        {step === 2 && (
                            <motion.div
                                key="step2"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                data-testid="booking-step-2"
                            >
                                <h2 className="font-anton text-2xl mb-2">ADD PRODUCTS</h2>
                                <p className="text-zinc-500 font-urbanist mb-6">Reserve your favorite El Patron products (optional)</p>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {EL_PATRON_PRODUCTS.map((product) => {
                                        const selected = selectedProducts.find(p => p.id === product.id);
                                        return (
                                            <div
                                                key={product.id}
                                                className={`p-4 border transition-all ${
                                                    selected
                                                        ? 'border-amber-500 bg-amber-500/10'
                                                        : 'border-zinc-800 bg-dark-paper'
                                                }`}
                                                data-testid={`product-select-${product.id}`}
                                            >
                                                <div className="flex justify-between items-center">
                                                    <div>
                                                        <h4 className="font-urbanist font-semibold text-white">
                                                            {product.name}
                                                        </h4>
                                                        <span className="font-anton text-lg text-amber-500">
                                                            €{product.price.toFixed(2).replace('.', ',')}
                                                        </span>
                                                    </div>
                                                    
                                                    {selected ? (
                                                        <div className="flex items-center gap-3">
                                                            <button
                                                                onClick={() => updateProductQuantity(product.id, -1)}
                                                                className="w-8 h-8 border border-amber-500 text-amber-500 flex items-center justify-center hover:bg-amber-500 hover:text-black"
                                                            >
                                                                <Minus size={16} />
                                                            </button>
                                                            <span className="font-anton text-lg w-8 text-center">{selected.quantity}</span>
                                                            <button
                                                                onClick={() => updateProductQuantity(product.id, 1)}
                                                                className="w-8 h-8 border border-amber-500 text-amber-500 flex items-center justify-center hover:bg-amber-500 hover:text-black"
                                                            >
                                                                <Plus size={16} />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => toggleProduct(product)}
                                                            className="px-4 py-2 border border-zinc-600 text-zinc-400 hover:border-amber-500 hover:text-amber-500 font-urbanist text-sm"
                                                        >
                                                            Add
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {selectedProducts.length > 0 && (
                                    <div className="mt-6 p-4 bg-zinc-900 border border-amber-500/30">
                                        <p className="text-zinc-400 font-urbanist text-sm">
                                            <span className="text-amber-500 font-semibold">{selectedProducts.length}</span> products • 
                                            Products total: <span className="text-amber-500 font-anton">€{totalProductPrice.toFixed(2)}</span>
                                        </p>
                                    </div>
                                )}

                                <p className="text-zinc-600 font-urbanist text-sm mt-4 text-center">
                                    Products will be ready for pickup at your appointment
                                </p>
                            </motion.div>
                        )}

                        {/* Step 3: Barber Selection */}
                        {step === 3 && (
                            <motion.div
                                key="step3"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                data-testid="booking-step-3"
                            >
                                <h2 className="font-anton text-2xl mb-6">CHOOSE YOUR BARBER</h2>
                                
                                {availableBarbers.length === 0 ? (
                                    <div className="text-center py-8">
                                        <p className="text-zinc-500 font-urbanist">
                                            No barbers available for this combination of services.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="grid md:grid-cols-2 gap-4">
                                        {availableBarbers.map((barber) => (
                                            <button
                                                key={barber.key}
                                                onClick={() => setSelectedBarber(barber)}
                                                data-testid={`barber-select-${barber.key}`}
                                                className={`text-left p-6 border transition-colors ${
                                                    selectedBarber?.key === barber.key
                                                        ? 'border-primary bg-primary/10'
                                                        : 'border-zinc-800 bg-dark-paper hover:border-zinc-600'
                                                }`}
                                            >
                                                {barber.image_url ? (
                                                    <img
                                                        src={barber.image_url}
                                                        alt={barber.name}
                                                        className="w-16 h-16 rounded-full object-cover mb-4"
                                                    />
                                                ) : (
                                                    <div className="w-16 h-16 bg-zinc-800 rounded-full flex items-center justify-center mb-4">
                                                        <User size={32} className="text-zinc-500" />
                                                    </div>
                                                )}
                                                <h4 className="font-urbanist font-semibold text-white text-lg">{barber.name}</h4>
                                                {barber.description && (
                                                    <p className="text-zinc-500 text-sm mt-1">
                                                        {barber.description}
                                                    </p>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </motion.div>
                        )}

                        {/* Step 4: Date & Time */}
                        {step === 4 && (
                            <motion.div
                                key="step4"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                data-testid="booking-step-4"
                            >
                                <h2 className="font-anton text-2xl mb-6">SELECT DATE & TIME</h2>
                                
                                <div className="grid md:grid-cols-2 gap-8">
                                    {/* Calendar */}
                                    <div className="bg-dark-paper border border-zinc-800 p-4">
                                        <CalendarComponent
                                            mode="single"
                                            selected={selectedDate}
                                            onSelect={setSelectedDate}
                                            disabled={(date) => {
                                                const day = date.getDay();
                                                const today = new Date();
                                                today.setHours(0, 0, 0, 0);
                                                return date < today || day === 0;
                                            }}
                                            className="text-white"
                                            data-testid="booking-calendar"
                                        />
                                    </div>

                                    {/* Time Slots */}
                                    <div>
                                        <h3 className="font-urbanist font-semibold text-zinc-400 mb-4">
                                            {selectedDate ? format(selectedDate, 'EEEE, MMMM d') : 'Select a date'}
                                        </h3>
                                        
                                        {selectedDate ? (
                                            timeSlots.length > 0 ? (
                                                <div className="grid grid-cols-3 gap-2 max-h-[300px] overflow-y-auto pr-2">
                                                    {timeSlots.map((slot) => (
                                                        <button
                                                            key={slot.time}
                                                            onClick={() => slot.available && setSelectedTime(slot.time)}
                                                            disabled={!slot.available}
                                                            data-testid={`time-slot-${slot.time}`}
                                                            className={`time-slot ${
                                                                selectedTime === slot.time ? 'selected' : ''
                                                            } ${!slot.available ? 'disabled' : ''}`}
                                                        >
                                                            {slot.time}
                                                        </button>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-zinc-600 font-urbanist">
                                                    Loading time slots...
                                                </p>
                                            )
                                        ) : (
                                            <p className="text-zinc-600 font-urbanist">
                                                Select a date to see available times
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {/* Step 5: Customer Details */}
                        {step === 5 && (
                            <motion.div
                                key="step5"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                data-testid="booking-step-5"
                            >
                                <h2 className="font-anton text-2xl mb-6">YOUR DETAILS</h2>
                                
                                <div className="grid md:grid-cols-2 gap-8">
                                    <div className="space-y-6">
                                        <div>
                                            <label className="block text-zinc-400 font-urbanist text-sm mb-2">
                                                Full Name *
                                            </label>
                                            <Input
                                                value={customerInfo.name}
                                                onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                                                placeholder="John Doe"
                                                data-testid="input-name"
                                                className="bg-transparent border-zinc-700 focus:border-primary text-white placeholder:text-zinc-600"
                                            />
                                        </div>
                                        
                                        <div>
                                            <label className="block text-zinc-400 font-urbanist text-sm mb-2">
                                                Email *
                                            </label>
                                            <Input
                                                type="email"
                                                value={customerInfo.email}
                                                onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                                                placeholder="john@example.com"
                                                data-testid="input-email"
                                                className="bg-transparent border-zinc-700 focus:border-primary text-white placeholder:text-zinc-600"
                                            />
                                        </div>
                                        
                                        <div>
                                            <label className="block text-zinc-400 font-urbanist text-sm mb-2">
                                                Phone Number *
                                            </label>
                                            <Input
                                                type="tel"
                                                value={customerInfo.phone}
                                                onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                                                placeholder="+356 1234 5678"
                                                required
                                                data-testid="input-phone"
                                                className="bg-transparent border-zinc-700 focus:border-primary text-white placeholder:text-zinc-600"
                                            />
                                            {!customerInfo.phone && customerInfo.name && (
                                                <p className="text-red-400 text-xs mt-1 font-urbanist">Il numero di telefono è obbligatorio per ricevere conferma e promemoria</p>
                                            )}
                                        </div>
                                        
                                        <div>
                                            <label className="block text-zinc-400 font-urbanist text-sm mb-2">
                                                Notes (Optional)
                                            </label>
                                            <Textarea
                                                value={customerInfo.notes}
                                                onChange={(e) => setCustomerInfo({ ...customerInfo, notes: e.target.value })}
                                                placeholder="Special requests..."
                                                data-testid="input-notes"
                                                className="bg-transparent border-zinc-700 focus:border-primary text-white placeholder:text-zinc-600 min-h-[100px]"
                                            />
                                        </div>
                                    </div>

                                    {/* Booking Summary */}
                                    <div className="bg-dark-paper border border-zinc-800 p-6 h-fit">
                                        <h3 className="font-anton text-lg mb-4">BOOKING SUMMARY</h3>
                                        
                                        {/* Services */}
                                        <div className="mb-4">
                                            <p className="text-zinc-500 text-sm font-urbanist mb-2">Services</p>
                                            {selectedServices.map(s => (
                                                <div key={s.key} className="flex justify-between text-sm mb-1">
                                                    <span className="text-white">{s.name}</span>
                                                    <span className="text-primary">€{s.price}</span>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Products */}
                                        {selectedProducts.length > 0 && (
                                            <div className="mb-4 pt-4 border-t border-zinc-800">
                                                <p className="text-zinc-500 text-sm font-urbanist mb-2">El Patron Products</p>
                                                {selectedProducts.map(p => (
                                                    <div key={p.id} className="flex justify-between text-sm mb-1">
                                                        <span className="text-white">{p.name} x{p.quantity}</span>
                                                        <span className="text-amber-500">€{(p.price * p.quantity).toFixed(2)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Details */}
                                        <div className="space-y-2 pt-4 border-t border-zinc-800">
                                            <div className="flex justify-between text-sm">
                                                <span className="text-zinc-500">Barber</span>
                                                <span className="text-white">{selectedBarber?.name}</span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className="text-zinc-500">Date</span>
                                                <span className="text-white">{selectedDate && format(selectedDate, 'MMM d, yyyy')}</span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className="text-zinc-500">Time</span>
                                                <span className="text-white">{selectedTime}</span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className="text-zinc-500">Duration</span>
                                                <span className="text-white">{totalDuration} min</span>
                                            </div>
                                        </div>

                                        {/* Total */}
                                        <div className="flex justify-between pt-4 mt-4 border-t border-zinc-800">
                                            <span className="text-zinc-400 font-urbanist">Total</span>
                                            <span className="font-anton text-2xl text-primary">€{totalPrice.toFixed(2)}</span>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {/* Step 6: Success */}
                        {step === 6 && (
                            <motion.div
                                key="step6"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="text-center py-12"
                                data-testid="booking-success"
                            >
                                <div className="w-24 h-24 bg-primary rounded-full flex items-center justify-center mx-auto mb-8">
                                    <Check size={48} className="text-black" />
                                </div>
                                <h2 className="font-anton text-4xl mb-4">BOOKING CONFIRMED!</h2>
                                <p className="font-urbanist text-xl text-zinc-400 mb-8">
                                    We&apos;ve sent a confirmation email to {customerInfo.email}
                                </p>
                                
                                <div className="bg-dark-paper border border-zinc-800 p-6 max-w-md mx-auto mb-8 text-left">
                                    <div className="space-y-3">
                                        <div>
                                            <p className="text-zinc-500 text-sm">Services</p>
                                            <p className="text-white">{selectedServices.map(s => s.name).join(', ')}</p>
                                        </div>
                                        {selectedProducts.length > 0 && (
                                            <div>
                                                <p className="text-zinc-500 text-sm">Products</p>
                                                <p className="text-amber-500">{selectedProducts.map(p => `${p.name} x${p.quantity}`).join(', ')}</p>
                                            </div>
                                        )}
                                        <div className="flex justify-between">
                                            <span className="text-zinc-500">Date & Time</span>
                                            <span className="text-white">
                                                {selectedDate && format(selectedDate, 'MMM d')} • {selectedTime}
                                            </span>
                                        </div>
                                        <div className="flex justify-between pt-3 border-t border-zinc-800">
                                            <span className="text-zinc-500">Total</span>
                                            <span className="font-anton text-xl text-primary">€{totalPrice.toFixed(2)}</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <Button
                                    onClick={() => navigate('/')}
                                    className="bg-primary text-black font-anton tracking-wider px-8 py-4"
                                    data-testid="back-to-home-btn"
                                >
                                    BACK TO HOME
                                </Button>

                                <div className="mt-6">
                                    <Button
                                        onClick={() => {
                                            const barberName = barbers.find(b => b.key === selectedBarber)?.name || 'RL Barbershop';
                                            const dateStr = selectedDate ? format(selectedDate, 'EEEE d MMMM yyyy') : '';
                                            const serviceNames = selectedServices.map(s => s.name).join(', ');
                                            const lang = (navigator.language || 'en').slice(0, 2);
                                            const msgs = {
                                                it: { title: 'Prenotazione confermata!', services: 'Servizi', date: 'Data', time: 'Ora', barber: 'Barbiere', total: 'Totale', bye: 'A presto!' },
                                                mt: { title: 'Booking ikkonfermata!', services: 'Servizzi', date: 'Data', time: 'Ħin', barber: 'Barbier', total: 'Total', bye: 'Narawna dalwaqt!' },
                                                en: { title: 'Booking confirmed!', services: 'Services', date: 'Date', time: 'Time', barber: 'Barber', total: 'Total', bye: 'See you soon!' },
                                                fr: { title: 'Réservation confirmée!', services: 'Services', date: 'Date', time: 'Heure', barber: 'Barbier', total: 'Total', bye: 'À bientôt!' },
                                                de: { title: 'Buchung bestätigt!', services: 'Dienstleistungen', date: 'Datum', time: 'Uhrzeit', barber: 'Barbier', total: 'Gesamt', bye: 'Bis bald!' },
                                                es: { title: '¡Reserva confirmada!', services: 'Servicios', date: 'Fecha', time: 'Hora', barber: 'Barbero', total: 'Total', bye: '¡Hasta pronto!' },
                                            };
                                            const t = msgs[lang] || msgs.en;
                                            const message = `✅ ${t.title}\n\n💈 *RL Barbershop - Swieqi*\n📋 ${t.services}: ${serviceNames}\n📅 ${t.date}: ${dateStr}\n🕐 ${t.time}: ${selectedTime}\n💇 ${t.barber}: ${barberName}\n💰 ${t.total}: €${totalPrice.toFixed(2)}\n\n${t.bye} 🤙`;
                                            const whatsappUrl = `https://wa.me/${customerInfo.phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
                                            window.open(whatsappUrl, '_blank');
                                        }}
                                        className="bg-green-600 hover:bg-green-700 text-white font-anton tracking-wider px-8 py-4 w-full"
                                        data-testid="whatsapp-reminder-btn"
                                    >
                                        <MessageCircle size={20} className="mr-2" />
                                        SEND WHATSAPP REMINDER
                                    </Button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Navigation Buttons */}
                    {step < 6 && (
                        <div className="flex justify-between mt-12">
                            <Button
                                variant="outline"
                                onClick={() => setStep(step - 1)}
                                disabled={step === 1}
                                data-testid="booking-prev-btn"
                                className="border-zinc-700 text-zinc-400 hover:text-white hover:border-white disabled:opacity-30"
                            >
                                <ChevronLeft className="mr-2" />
                                Back
                            </Button>
                            
                            {step < 5 ? (
                                <Button
                                    onClick={() => setStep(step + 1)}
                                    disabled={!canProceed()}
                                    data-testid="booking-next-btn"
                                    className="bg-primary text-black font-anton tracking-wider hover:bg-white disabled:opacity-30"
                                >
                                    Next
                                    <ChevronRight className="ml-2" />
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleSubmit}
                                    disabled={!canProceed() || submitting}
                                    data-testid="booking-confirm-btn"
                                    className="bg-primary text-black font-anton tracking-wider hover:bg-white disabled:opacity-30 neon-glow"
                                >
                                    {submitting ? 'Booking...' : 'CONFIRM BOOKING'}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BookingPage;
